import { ConfiguratorProps } from 'views/ProductPage/configurators/index';
import { rm } from 'utils';
import { ChangeEvent, ChangeEventHandler, Reducer, useReducer } from 'react';
import { Input } from 'components/Shared/Form';
import connect from 'components/Shared/connect';
import { isValidZipcode, ZIP_CODE_LENGTH } from 'views/CheckoutPage/validation';
import { ZipCodeExcludeConfigurator as ZipCodeExcludeConfiguratorModel } from 'models/InstallationConfigurator';
import styles from './styles.scss';
import { onDeleteValue, onSelectValue } from 'views/ProductPage/configurators/actions';
import { getConfiguratorVariables } from 'views/ProductPage/selector';
import { LoadingStatus } from 'models/LoadingStatus';
import { handleServerError } from 'components/Shared/server';
import { IBasicResponse } from 'models/IBasicResponse';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { getCheckZipCodeAvailability } from 'services/ApiProductPageService';
interface OwnProps extends ConfiguratorProps {
  configurator: ZipCodeExcludeConfiguratorModel;
}

interface MergedProps extends OwnProps {
  zipCode: string;
  zipCodeList: string;
  setZipCode: ChangeEventHandler<HTMLInputElement>;
  handleError(response: IBasicResponse): void;
}

interface OwnState {
  valid: boolean;
  loadingStatus: LoadingStatus;
}

const initialState: OwnState = {
  valid: false,
  loadingStatus: LoadingStatus.Idle
};

type OwnAction =
  | { type: 'checkZipCodeAvailability' }
  | { type: 'checkZipCodeAvailabilitySuccess'; valid: boolean };

const ownReducer: Reducer<OwnState, OwnAction> = (state, action) => {
  switch (action.type) {
    case 'checkZipCodeAvailability':
      return {
        ...state,
        loadingStatus: LoadingStatus.Fetching,
        valid: false
      };
    case 'checkZipCodeAvailabilitySuccess':
      return {
        ...state,
        loadingStatus: LoadingStatus.Completed,
        valid: action.valid
      };
    default:
      return state;
  }
};

const trimZipcode = (zip: string) => {
  return zip.replace(/\s/g, '');
};

interface ZipValidationProps {
  state: OwnState;
  zipCode: string;
}

const getZipcodeMessage = ({ state, zipCode }: ZipValidationProps) => {
  const isValid = state.valid && isValidZipcode(zipCode);
  const isLongEnough = zipCode.length >= ZIP_CODE_LENGTH;
  const invalidZipcode = isLongEnough && !isValidZipcode(zipCode);

  return isValid
    ? rm('product_configurator_zipcodeexclude_can_deliver')
    : invalidZipcode
      ? rm('product_configurator_zipcodeexclude_invalid_zipcode')
      : !isValid
        ? rm('product_configurator_zipcodeexclude_can_not_deliver')
        : '';
};

const ZipcodeMessage = ({ zipCode, state }: ZipValidationProps) => {
  const trimmedZipCode = trimZipcode(zipCode);
  const isLongEnough = trimmedZipCode.length >= ZIP_CODE_LENGTH;
  const isLoading = state.loadingStatus !== LoadingStatus.Completed;
  const shouldRenderMessage = !isLoading && isLongEnough;

  const zipcodeMessage = shouldRenderMessage
    ? getZipcodeMessage({ zipCode: trimmedZipCode, state })
    : '';

  return <p className={styles.description}>{zipcodeMessage}</p>;
};

const ZipCodeExcludeConfigurator = ({
  zipCode,
  zipCodeList,
  setZipCode,
  handleError
}: MergedProps) => {
  const [state, dispatch] = useReducer(ownReducer, initialState);
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setZipCode(e);
    const shouldCheckZipCode =
      e.target.value.length >= ZIP_CODE_LENGTH && state.loadingStatus !== LoadingStatus.Fetching;
    if (shouldCheckZipCode) {
      dispatch({ type: 'checkZipCodeAvailability' });
      getCheckZipCodeAvailability(trimZipcode(e.target.value), zipCodeList)
        .then(res => {
          dispatch({ type: 'checkZipCodeAvailabilitySuccess', valid: res.valid });
        })
        .catch(handleError);
    }
  };
  return (
    <div className={styles.base}>
      <h3 className={styles.heading}>{rm('product_configurator_zipcodeexclude_header')}</h3>
      <p className={styles.description}>{rm('product_configurator_zipcodeexclude_body')}</p>
      <Input
        className={styles.zipInput}
        placeholder={rm('product_polarpump_installation_zipcode_placeholder')}
        title={rm('product_polarpump_installation_zipcode_header')}
        loading={state.loadingStatus === LoadingStatus.Fetching}
        max={ZIP_CODE_LENGTH}
        onChange={handleChange}
        value={zipCode}
        inputMode="numeric"
      />
      <ZipcodeMessage zipCode={zipCode} state={state} />
    </div>
  );
};

interface StateProps {
  inputs: ConfiguratorBase['values'];
}
export default connect<MergedProps, OwnProps, StateProps>(
  state => ({
    inputs: getConfiguratorVariables(state.currentPage)
  }),
  dispatch => ({
    setZipCode: ({ target: { value } }) =>
      dispatch(value ? onSelectValue('zipCode', value) : onDeleteValue('zipCode')),
    handleError: (response: IBasicResponse) => handleServerError(dispatch)(response)
  }),
  ({ inputs, ...state }, dispatch, props) => ({
    ...state,
    ...dispatch,
    ...props,
    zipCode: (inputs && inputs.zipCode) || '',
    zipCodeList: (inputs && inputs.zipCodeList) || ''
  })
)(ZipCodeExcludeConfigurator);
